import {
  BarChart3Icon,
  ChevronDownIcon,
  EyeIcon,
  HomeIcon,
  LayoutDashboardIcon,
  SearchIcon,
  SettingsIcon,
  UserIcon,
} from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { CustomerIdentityPreview } from './types/types';
import { getLogoImageUrl } from './utils/logo';

type CustomerIdentityLivePreviewProps = {
  isCollapsed?: boolean;
  onToggle?: () => void;
  preview: CustomerIdentityPreview | null;
};

const DEFAULT_PREVIEW_COLORS = {
  primary: 'hsl(var(--primary-color-light))',
  secondary: 'hsl(var(--primary-color))',
} as const;

const withAlpha = (color: string, hexAlpha: string, hslAlpha: number): string => {
  if (color.startsWith('#')) {
    return `${color}${hexAlpha}`;
  }

  return color.replace(/\)$/, ` / ${hslAlpha})`);
};

export const CustomerIdentityLivePreview = ({
  isCollapsed = false,
  onToggle,
  preview,
}: CustomerIdentityLivePreviewProps) => {
  const { t } = useTranslation();

  if (!preview) {
    return (
      <aside className="flex flex-col gap-3">
        <PreviewTitle isCollapsed={isCollapsed} onToggle={onToggle} />
        {isCollapsed ? null : (
          <div className="flex h-44 flex-col items-center justify-center rounded-sm border border-border bg-white text-center">
            <EyeIcon aria-hidden="true" className="mb-4 size-9 text-background-darker" />
            <p className="text-sm font-semibold leading-5 text-text-secondary">
              {t('noTenantSelectedForPreview')}
            </p>
            <p className="mt-1 max-w-60 text-xs font-medium leading-5 text-text-secondary">
              {t('selectTenantForLivePreview')}
            </p>
          </div>
        )}
      </aside>
    );
  }

  const primaryColor = preview.primaryColor ?? DEFAULT_PREVIEW_COLORS.primary;
  const secondaryColor = preview.secondaryColor ?? DEFAULT_PREVIEW_COLORS.secondary;
  const logoUrl =
    preview.logoPreviewUrl ?? (preview.logoId ? getLogoImageUrl(preview.logoId) : null);

  return (
    <aside className="flex flex-col gap-3">
      <PreviewTitle tenantName={preview.name} isCollapsed={isCollapsed} onToggle={onToggle} />

      {isCollapsed ? null : (
        <div className="overflow-hidden rounded-sm border border-border bg-white">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <div className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-background-lighter">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={t('tenantLogoAlt', { tenantName: preview.name })}
                  className="h-full w-full object-contain"
                />
              ) : (
                <span className="text-xs font-bold text-primary-darker">R</span>
              )}
            </div>

            <div className="min-w-0">
              <p className="truncate text-[10px] font-bold leading-3 text-primary-darker">
                Connect
              </p>
              <div
                className="mt-1 h-1 w-9 rounded-full"
                style={{ backgroundColor: primaryColor }}
              />
            </div>

            <div className="ml-auto flex h-6 flex-1 items-center gap-2 rounded-sm bg-background-lighter px-2">
              <SearchIcon aria-hidden="true" className="size-3 text-text-secondary" />
              <span className="text-[10px] font-semibold text-text-secondary">{t('search')}</span>
            </div>

            <div
              className="flex size-7 items-center justify-center rounded-full text-xs font-bold text-primary-darker"
              style={{ backgroundColor: withAlpha(primaryColor, '1F', 0.12) }}
            >
              {preview.name.charAt(0).toUpperCase()}
            </div>
          </div>

          <div className="grid grid-cols-[48px_1fr]">
            <nav className="flex flex-col items-center gap-2 border-r border-border bg-background-lighter/40 py-2">
              <PreviewNavItem
                icon={<LayoutDashboardIcon aria-hidden="true" />}
                label={t('dashboard')}
              />
              <PreviewNavItem
                icon={<HomeIcon aria-hidden="true" />}
                label={t('estates')}
                activeColor={primaryColor}
              />
              <PreviewNavItem icon={<BarChart3Icon aria-hidden="true" />} label={t('analytics')} />
              <PreviewNavItem icon={<UserIcon aria-hidden="true" />} label={t('profile')} />
              <PreviewNavItem icon={<SettingsIcon aria-hidden="true" />} label={t('setup')} />
            </nav>

            <div className="p-2">
              <div className="grid grid-cols-3 gap-2">
                <PreviewCard color={primaryColor} />
                <PreviewCard color={secondaryColor} />
                <PreviewCard />
              </div>

              <div className="mt-2 rounded-sm border border-border p-2">
                <div className="mb-2 h-2 w-24 rounded-full bg-background-darker" />
                <div className="space-y-2">
                  <PreviewLine className="w-32" />
                  <PreviewLine className="w-24" />
                  <PreviewLine className="w-36" />
                </div>
              </div>

              <div className="mt-2 flex items-center gap-2">
                <PreviewButton color={primaryColor}>{t('save')}</PreviewButton>
                <PreviewButton color={secondaryColor} variant="outline">
                  {t('cancel')}
                </PreviewButton>
                <span className="ml-auto flex min-w-0 items-center gap-1 text-[10px] font-semibold text-primary-darker">
                  <span
                    aria-hidden="true"
                    className="size-3 shrink-0 rounded-full"
                    style={{ backgroundColor: withAlpha(primaryColor, '66', 0.4) }}
                  />
                  <span className="truncate">{preview.name}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 border-t border-border px-2 py-2 text-[10px] font-semibold text-text-secondary">
            <PreviewLegend
              label={t('primaryColor')}
              color={primaryColor}
              value={preview.primaryColor ?? '-'}
            />
            <PreviewLegend
              label={t('secondaryColor')}
              color={secondaryColor}
              value={preview.secondaryColor ?? '-'}
            />
          </div>
        </div>
      )}
    </aside>
  );
};

const PreviewTitle = ({
  isCollapsed = false,
  onToggle,
  tenantName,
}: {
  isCollapsed?: boolean;
  onToggle?: () => void;
  tenantName?: string;
}) => {
  const { t } = useTranslation();

  const content = (
    <>
      <EyeIcon aria-hidden="true" className="size-4 shrink-0 text-text-secondary" />
      <h2 className="text-sm font-bold leading-5">{t('livePreview')}</h2>
      {tenantName ? (
        <span className="min-w-0 truncate rounded-full bg-background-lighter px-3 py-1 text-xs font-bold text-text-secondary">
          {tenantName}
        </span>
      ) : null}
      {onToggle ? (
        <ChevronDownIcon
          aria-hidden="true"
          className={`ml-auto size-4 shrink-0 text-text-secondary transition-transform ${
            isCollapsed ? '-rotate-90' : ''
          }`}
        />
      ) : null}
    </>
  );

  if (!onToggle) {
    return <div className="flex min-w-0 items-center gap-2 text-primary-darker">{content}</div>;
  }

  return (
    <button
      type="button"
      aria-expanded={!isCollapsed}
      aria-label={t(isCollapsed ? 'expandLivePreview' : 'collapseLivePreview')}
      className="flex min-w-0 items-center gap-2 rounded-sm text-left text-primary-darker transition-colors hover:text-primary"
      onClick={onToggle}
    >
      {content}
    </button>
  );
};

type PreviewNavItemProps = {
  activeColor?: string;
  icon: ReactElement;
  label: string;
};

const PreviewNavItem = ({ activeColor, icon, label }: PreviewNavItemProps) => (
  <div className="flex flex-col items-center gap-0.5 text-[8px] font-semibold text-text-secondary">
    <div
      className="flex size-6 items-center justify-center rounded-sm"
      style={{ backgroundColor: activeColor ? withAlpha(activeColor, '1F', 0.12) : undefined }}
    >
      <span className="[&_svg]:size-3" style={{ color: activeColor }}>
        {icon}
      </span>
    </div>
    <span>{label}</span>
  </div>
);

const PreviewCard = ({ color }: { color?: string }) => (
  <div className="h-12 rounded-sm border border-border p-2">
    <div
      className="mb-2 h-1.5 w-12 rounded-full bg-background-darker"
      style={{ backgroundColor: color ? withAlpha(color, '1F', 0.12) : undefined }}
    />
    <div
      className="h-3.5 rounded-sm bg-background-lighter"
      style={{ backgroundColor: color ? withAlpha(color, '14', 0.08) : undefined }}
    />
  </div>
);

const PreviewLine = ({ className }: { className: string }) => (
  <div className={`h-2 rounded-full bg-background-lighter ${className}`} />
);

type PreviewButtonProps = {
  children: ReactNode;
  color: string;
  variant?: 'solid' | 'outline';
};

const PreviewButton = ({ children, color, variant = 'solid' }: PreviewButtonProps) => (
  <span
    className="inline-flex h-6 items-center rounded-sm border px-3 text-xs font-bold"
    style={
      variant === 'solid'
        ? {
            backgroundColor: color,
            borderColor: color,
            color: 'white',
          }
        : {
            backgroundColor: 'transparent',
            borderColor: color,
            color,
          }
    }
  >
    {children}
  </span>
);

type PreviewLegendProps = {
  color: string;
  label: string;
  value: string;
};
const PreviewLegend = ({ color, label, value }: PreviewLegendProps) => (
  <span className="flex min-w-0 items-center gap-1">
    <span
      aria-hidden="true"
      className="size-2 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
    <span className="truncate">
      {label}: {value}
    </span>
  </span>
);
